import { ReactNode } from 'react';

import { OPEN_ERROR_MODAL } from '@iwModals/Modals.actions';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleRight, IconInfoCircleFilled } from '@iwoca/icons';
import {
    fetchPutFormalOffersCreditFacilityConfiguration,
    fetchPutFormalOffersFlexi24Configuration
} from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import messages from './RecommendedOfferView.messages';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { buildLocationWithSearch } from '../../../drawDown/DrawDownHelper';
import { ChooseLoanAmount } from '../../../sharedDashboardComponents/slider/chooseLoanAmount/ChooseLoanAmount';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    startDrawdown: ({
        offerId,
        drawdownAmount
    }: {
        offerId: string;
        drawdownAmount: number;
    }) => {
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            //@ts-ignore
            location: buildLocationWithSearch({
                pathname: '/funding-requirements/',
                offerId: offerId,
                amount: drawdownAmount
            })
        });
    },
    openErrorModal: () =>
        dispatch({ type: OPEN_ERROR_MODAL, id: 'FallbackErrorModal' })
});

type TRecommendedOfferContentProps = ReturnType<typeof mapDispatchToProps> & {
    offerId: string;
    loanDuration: number;
    drawdownAmount: number;
    minimumDrawdownAmount: number;
    maximumDrawdownAmount: number;
    offerTimeout: string;
    productType: string;
    setDrawdownAmount: (drawdownAmount: number) => void;
    hideDisclaimer?: boolean;
    customTitle?: ReactNode;
    interestOnlyPeriod?: number;
};

const RecommendedOfferContent = ({
    offerId,
    loanDuration,
    drawdownAmount,
    minimumDrawdownAmount,
    maximumDrawdownAmount,
    setDrawdownAmount,
    startDrawdown,
    productType,
    openErrorModal,
    hideDisclaimer,
    customTitle,
    interestOnlyPeriod
}: TRecommendedOfferContentProps) => {
    const stateKey = useStateKey();

    const handleSubmit = async () => {
        try {
            tracking.ampli.drawdownCtaClicked();
            if (productType === 'credit_facility') {
                await fetchPutFormalOffersCreditFacilityConfiguration({
                    stateKey,
                    offerId: offerId,
                    body: { data: { cash_amount: drawdownAmount } }
                });
            } else if (productType === 'flexi24') {
                await fetchPutFormalOffersFlexi24Configuration({
                    stateKey,
                    offerId: offerId,
                    body: { data: { cash_amount: drawdownAmount } }
                });
            }
            startDrawdown({ drawdownAmount: drawdownAmount, offerId: offerId });
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (err) {
            openErrorModal();
        }
    };

    return (
        <div className="w-[650px] border-r border-secondary-90 px-xl py-m pr-l max-sm:mb-l max-sm:w-full max-sm:rounded-s max-sm:border-none max-sm:bg-structure-100">
            {customTitle ? (
                customTitle
            ) : (
                <h2>
                    <FormattedMessage
                        id="_.recommended_offer_view.card_title"
                        defaultMessage="Repay over {duration} months"
                        values={{ duration: <span>{loanDuration}</span> }}
                    />
                </h2>
            )}

            <ChooseLoanAmount
                updateDrawdownAmount={setDrawdownAmount}
                drawdownAmount={drawdownAmount}
                minimumDrawdownAmount={minimumDrawdownAmount}
                maximumDrawdownAmount={maximumDrawdownAmount}
                label={messages.withdrawalAmountLabel}
            />
            {!hideDisclaimer && (
                <div className="flex flex-row gap-s rounded-s border border-secondary-80 bg-secondary-95 p-m text-secondary-40">
                    <IconInfoCircleFilled className="size-[18px] shrink-0 fill-secondary-40" />
                    <div className="text-s">
                        {interestOnlyPeriod === 6 ? (
                            <div>
                                <span className="block font-med">
                                    <FormattedMessage
                                        id="_.offer_view.interest_only_plus_disclaimer_title"
                                        defaultMessage="How does it work?"
                                    />
                                </span>
                                <span className="font-reg">
                                    <FormattedMessage
                                        id="_.offer_view.interest_only_plus_disclaimer_text"
                                        // The German translation for this message is different to the falsy case of this ternary, this is just a placeholder
                                        defaultMessage="You will have full flexibility to repay early for free, and the ability to access additional funds at a later date too."
                                    />
                                </span>
                            </div>
                        ) : (
                            <FormattedMessage
                                id="_.recommended_offer_view.disclaimer_text"
                                defaultMessage="You will have full flexibility to repay early for free, and the ability to access additional funds at a later date too."
                            />
                        )}
                    </div>
                </div>
            )}
            <Button className="my-xl w-full" onClick={() => handleSubmit()}>
                <FormattedMessage {...messages.CTAText} />
                <IconAngleRight fontSize={20} className="fill-structure-100" />
            </Button>
        </div>
    );
};

export const RecommendedOfferContentUI = connect(
    null,
    mapDispatchToProps
)(RecommendedOfferContent);
